<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import MemberInfoHeader from '@/components/collectivites/MemberInfoHeader.vue';
import membersService from '@/services/membersService';
import { formatAmount, formatDateFrench } from '@/utils/formatters';

const route = useRoute();
const router = useRouter();

const memberId = computed(() => Number(route.params.id));
const member = ref<any>(null);
const wallet = ref<any>(null);
const cartes = ref<any[]>([]);
const transactions = ref<any[]>([]);
const loading = ref(false);
const loadingTransactions = ref(false);
const errorMessage = ref('');

function goBack() {
    router.push({ name: 'collectivites-membres' });
}

async function loadAll() {
    if (!memberId.value) return;
    loading.value = true;
    errorMessage.value = '';
    try {
        const [m, w, c] = await Promise.all([
            membersService.getMember(memberId.value),
            membersService.getMemberWalletInfos(memberId.value).catch(() => null),
            membersService.getMemberCartes(memberId.value).catch(() => ({ cartes: [] } as any)),
        ]);
        member.value = m;
        wallet.value = w;
        cartes.value = c?.cartes || [];

        if (cartes.value.length > 0) {
            loadTransactions(cartes.value[0].id);
        }
    } catch (e: any) {
        errorMessage.value = e?.message || 'Erreur lors du chargement.';
    } finally {
        loading.value = false;
    }
}

async function loadTransactions(carteId: number) {
    loadingTransactions.value = true;
    try {
        const res = await membersService.getMemberCarteTransactions(memberId.value, carteId, {});
        transactions.value = res?.transactions || [];
    } catch (e) {
        transactions.value = [];
    } finally {
        loadingTransactions.value = false;
    }
}

onMounted(loadAll);

const headerProgress = computed(() => {
    if (!wallet.value) return null;
    return {
        label: 'Utilisation',
        percent: wallet.value.utilise_percent ?? 0,
        valueLabel: `${Math.round(wallet.value.utilise_percent ?? 0)}%`,
    };
});
</script>

<template>
    <section class="container-full-1 bg-grey-50">
        <div class="d-flex align-items-center gap-4 mb-6">
            <button class="btn white2" @click="goBack">
                <img src="/public/icons/icon-back.svg" class="btn-icon" alt="" />
                <span>Retour</span>
            </button>
            <h2 class="secondary-font gap-4 mt-2 mb-0">Membre · Utilisation</h2>
        </div>

        <div v-if="loading" class="flex-center py-10"><div class="loader dark"></div></div>
        <div v-if="errorMessage" class="alert alert-danger mb-4">{{ errorMessage }}</div>

        <MemberInfoHeader
            :member="member"
            :end-date="member?.souscription?.date_fin"
            :progress="headerProgress"
        />

        <div class="clear-both h-20"></div>

        <!-- Wallet / Utilisation -->
        <div class="widget edit-card">
            <h3 class="edit-card-title">
                <img src="/public/icons/icon-data.svg" class="size-base" alt="" />
                Wallet · Utilisation
            </h3>

            <div class="stats-grid">
                <div>
                    <h5 class="stat-label">
                        <span class="circle-medium bg-color-1000"></span>
                        Solde
                    </h5>
                    <h1 class="stat-value">{{ wallet ? formatAmount(wallet.solde) + ' F' : '-' }}</h1>
                    <h5 class="text-grey-600 mb-4">{{ wallet?.solde_percent ?? 0 }}%</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-color-1000 text-white" :style="`width: ${Math.max(2, Math.min(100, wallet?.solde_percent || 0))}%;`">
                            {{ Math.round(wallet?.solde_percent || 0) }}%
                        </div>
                    </div>
                </div>
                <div>
                    <h5 class="stat-label">
                        <span class="circle-medium bg-purple"></span>
                        Montant utilisé
                    </h5>
                    <h1 class="stat-value">{{ wallet ? formatAmount(wallet.utilise) + ' F' : '-' }}</h1>
                    <h5 class="text-grey-600 mb-4">{{ wallet?.utilise_percent ?? 0 }}%</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-purple text-white" :style="`width: ${Math.max(2, Math.min(100, wallet?.utilise_percent || 0))}%;`">
                            {{ Math.round(wallet?.utilise_percent || 0) }}%
                        </div>
                    </div>
                </div>
                <div>
                    <h5 class="stat-label">
                        <span class="circle-medium bg-green-light"></span>
                        Dernier décaissement
                    </h5>
                    <h1 class="stat-value">{{ wallet?.dernier_decaissement ? formatAmount(wallet.dernier_decaissement) + ' F' : '-' }}</h1>
                    <h5 class="text-grey-600 mb-4">{{ wallet?.dernier_decaissement_date ? formatDateFrench(wallet.dernier_decaissement_date) : '-' }}</h5>
                    <div class="stat-progress">
                        <div class="stat-progress-fill bg-green-light" style="width: 100%;">100%</div>
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-20"></div>

        <!-- Transactions -->
        <div class="widget edit-card">
            <h3 class="edit-card-title">
                <img src="/public/icons/icon-data.svg" class="size-base" alt="" />
                Transactions
            </h3>

            <div v-if="cartes.length > 1" class="flex gap-3 mb-4" style="flex-wrap: wrap;">
                <button
                    v-for="(c, i) in cartes"
                    :key="c.id"
                    class="btn"
                    :class="{ 'white2 active': i === 0, light2: i !== 0 }"
                    @click="loadTransactions(c.id)"
                >
                    {{ c.alias || c.bloc4 || `Carte ${i + 1}` }}
                </button>
            </div>

            <div v-if="cartes.length === 0" class="text-center text-grey-500 py-5">
                Aucune carte associée à ce membre.
            </div>

            <div v-else-if="loadingTransactions" class="text-center py-5">
                <div class="loader dark"></div>
            </div>

            <div v-else-if="transactions.length === 0" class="text-center text-grey-500 py-5">
                Aucune transaction à afficher.
            </div>

            <div v-else>
                <div v-for="tx in transactions" :key="tx.id" class="transaction-row">
                    <div class="transaction-icon">
                        <img src="/public/icons/icon-trans-recharger.svg" alt="" />
                    </div>
                    <div class="transaction-body">
                        <h6 class="font-semibold mb-1">{{ tx.libelle || tx.label || 'Transaction' }}</h6>
                        <p class="text-sm text-grey-600 mb-0">
                            {{ tx.created_at ? formatDateFrench(tx.created_at) : '' }}
                            <span v-if="tx.reference"> · {{ tx.reference }}</span>
                        </p>
                    </div>
                    <div class="transaction-amount" :class="{ positive: (tx.montant || 0) > 0 }">
                        {{ (tx.montant || 0) > 0 ? '+' : '' }}{{ formatAmount(tx.montant || 0) }} F
                    </div>
                </div>
            </div>
        </div>

        <div class="clear-both h-50"></div>
    </section>
</template>

<style scoped>
.edit-card {
    padding: 1.75rem;
}

.edit-card-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgb(var(--primary-color-rgb), 1);
    font-weight: 600;
    margin: 0 0 1.25rem;
}

.stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.5rem;
}

.stat-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: rgba(0, 0, 0, 0.6);
    font-weight: 600;
    margin-bottom: 0.5rem;
}

.stat-value {
    font-size: 2rem;
    font-weight: 700;
    margin: 0.25rem 0 0.5rem;
}

.stat-progress {
    height: 36px;
    border-radius: 100px;
    background-color: rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.stat-progress-fill {
    height: 100%;
    border-radius: 100px;
    display: flex;
    align-items: center;
    padding: 0 1rem;
    font-weight: 600;
    transition: width 0.3s ease;
}

.transaction-row {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.transaction-row:last-child { border-bottom: none; }

.transaction-icon {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background-color: rgba(0, 185, 172, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.transaction-icon img {
    width: 24px;
    height: 24px;
}

.transaction-body {
    flex: 1 1 auto;
    min-width: 0;
}

.transaction-body p {
    word-break: break-word;
}

.transaction-amount {
    font-weight: 700;
    flex-shrink: 0;
    color: rgba(0, 0, 0, 0.8);
}

.transaction-amount.positive {
    color: rgb(var(--color-success-rgb));
}

.circle-medium {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 50%;
}
</style>
